import type { PerTableStats, PlayerStatistics, SaveData } from '@/types';
import { getRankForXP, type Rank } from '@/data/ranks';
import { createDefaultSaveStore, SupabaseSaveStore, type SaveStore } from '@/systems/SaveStore';

const SAVE_VERSION = 1;

/* ------------------------------------------------------------------ */
/* Defaults                                                            */
/* ------------------------------------------------------------------ */

function generatePlayerId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `pid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createDefaultSaveData(now: number = Date.now()): SaveData {
  return {
    version: SAVE_VERSION,
    playerId: generatePlayerId(),
    playerName: 'Geek',
    avatarId: 'default',
    totalXP: 0,
    currentRank: 1,
    starsPerLevel: {},
    statistics: {
      totalQuestions: 0,
      totalCorrect: 0,
      totalCorrectResponseTimeMs: 0,
      weakestTable: 1,
      strongestTable: 1,
      streakRecord: 0,
      perTable: {},
    },
    achievements: [],
    createdAt: now,
    lastPlayedAt: now,
    timeAttackBestScore: 0,
  };
}

/* ------------------------------------------------------------------ */
/* SessionInput / Outcome                                              */
/* ------------------------------------------------------------------ */

export interface SessionInput {
  /** Pontos somados nesta rodada — convertem 1:1 em XP. */
  scoreGained: number;
  correctCount: number;
  wrongCount: number;
  /** Soma dos tempos de resposta das corretas (ms). */
  totalCorrectResponseTimeMs: number;
  /** Maior combo atingido nesta rodada. */
  maxCombo: number;
  /** Stats por tabuada nesta rodada. */
  perTable: Record<number, PerTableStats>;
  /** Identificador da fase (se aplicável) e estrelas alcançadas. */
  levelKey?: string;
  stars?: 0 | 1 | 2 | 3;
}

export interface SessionOutcome {
  xpGained: number;
  newTotalXP: number;
  rankBefore: Rank;
  rankAfter: Rank;
  rankUp: boolean;
}

/* ------------------------------------------------------------------ */
/* SaveSystem                                                          */
/* ------------------------------------------------------------------ */

/**
 * SaveSystem — fonte da verdade do progresso do jogador.
 *
 * Carrega o save (ou cria um novo na primeira execução), expõe getters
 * síncronos pra UI consumir, e oferece `recordSession` que aplica o
 * resultado de uma rodada e persiste.
 *
 * Persistência é assíncrona, mas o estado em memória é sempre síncrono
 * — então cenas podem renderizar XP/rank sem await.
 */
export class SaveSystem {
  private data: SaveData | null = null;
  private store: SaveStore;

  constructor(store: SaveStore = createDefaultSaveStore()) {
    this.store = store;
  }

  /** Carrega do store; se não houver, cria um save default e persiste. */
  async init(): Promise<void> {
    const loaded = await this.store.load();
    if (loaded) {
      this.data = this.migrate(loaded);
    } else {
      this.data = createDefaultSaveData();
      await this.store.save(this.data);
    }
  }

  /**
   * Vincula a instância a um aluno específico — fluxo padrão em runtime
   * (BootScene não inicializa direto). Substitui a Store por uma
   * SupabaseSaveStore atrelada ao student_id e carrega o progresso. Se
   * a row de progresso não existir (ex: aluno recém-criado mas trigger
   * SQL falhou), cria um default com playerId = studentId pra que
   * SupabaseSaveStore aceite o upsert subsequente.
   */
  async bindToStudent(studentId: string): Promise<void> {
    this.store = new SupabaseSaveStore(studentId);
    const loaded = await this.store.load();
    if (loaded) {
      this.data = this.migrate(loaded);
    } else {
      this.data = { ...createDefaultSaveData(), playerId: studentId };
      await this.store.save(this.data);
    }
  }

  getData(): SaveData {
    return this.requireData();
  }

  getCurrentRank(): Rank {
    return getRankForXP(this.requireData().totalXP);
  }

  async setProfile(updates: Partial<Pick<SaveData, 'playerName' | 'avatarId'>>): Promise<void> {
    const data = this.requireData();
    if (updates.playerName !== undefined) data.playerName = updates.playerName;
    if (updates.avatarId !== undefined) data.avatarId = updates.avatarId;
    data.lastPlayedAt = Date.now();
    await this.store.save(deepCloneSave(data));
  }

  /**
   * Aplica o resultado de uma rodada: soma XP, atualiza stats agregados,
   * recalcula tabuada mais fraca/forte, persiste e devolve um resumo
   * (incluindo se houve rank up).
   */
  async recordSession(input: SessionInput): Promise<SessionOutcome> {
    const data = this.requireData();

    const rankBefore = getRankForXP(data.totalXP);
    const xpGained = Math.max(0, Math.round(input.scoreGained));
    data.totalXP += xpGained;
    const rankAfter = getRankForXP(data.totalXP);
    data.currentRank = rankAfter.level;

    data.statistics = mergeStatistics(data.statistics, input);
    data.lastPlayedAt = Date.now();

    if (input.levelKey && input.stars !== undefined) {
      const previous = data.starsPerLevel[input.levelKey] ?? 0;
      // Mantém o melhor desempenho (não regride com tentativas piores).
      data.starsPerLevel[input.levelKey] = Math.max(previous, input.stars);
    }

    await this.store.save(deepCloneSave(data));

    return {
      xpGained,
      newTotalXP: data.totalXP,
      rankBefore,
      rankAfter,
      rankUp: rankAfter.level > rankBefore.level,
    };
  }

  /**
   * Compara o score atual com o recorde do Contra o Tempo. Atualiza e
   * persiste se for novo recorde.
   */
  async updateTimeAttackBest(score: number): Promise<TimeAttackBestOutcome> {
    const data = this.requireData();
    const previous = data.timeAttackBestScore;
    const sanitized = Math.max(0, Math.round(score));
    const newRecord = sanitized > previous;
    if (newRecord) {
      data.timeAttackBestScore = sanitized;
      data.lastPlayedAt = Date.now();
      await this.store.save(deepCloneSave(data));
    }
    return { newRecord, previous, current: data.timeAttackBestScore };
  }

  /** Reseta o save para os defaults — útil para "Excluir minha conta" (§15). */
  async clear(): Promise<void> {
    this.data = createDefaultSaveData();
    await this.store.save(this.data);
  }

  /** Devolve `data` ou lança — atalho para os métodos públicos. */
  private requireData(): SaveData {
    if (!this.data) {
      throw new Error('SaveSystem não inicializado — chame init() antes.');
    }
    return this.data;
  }

  /**
   * Migrações entre versões. Saves anteriores podem não ter campos novos
   * (ex: timeAttackBestScore foi adicionado depois do v1). Preenchemos
   * defensivamente com defaults — sem isso o jogo quebraria com `undefined`
   * em propriedades obrigatórias.
   */
  private migrate(loaded: SaveData): SaveData {
    const partial = loaded as Partial<SaveData>;
    return {
      ...loaded,
      version: SAVE_VERSION,
      timeAttackBestScore: partial.timeAttackBestScore ?? 0,
    };
  }
}

export interface TimeAttackBestOutcome {
  newRecord: boolean;
  previous: number;
  current: number;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function mergeStatistics(prev: PlayerStatistics, input: SessionInput): PlayerStatistics {
  const totalQuestions = prev.totalQuestions + input.correctCount + input.wrongCount;
  const totalCorrect = prev.totalCorrect + input.correctCount;
  const totalCorrectResponseTimeMs = prev.totalCorrectResponseTimeMs + input.totalCorrectResponseTimeMs;
  const streakRecord = Math.max(prev.streakRecord, input.maxCombo);

  const perTable: Record<number, PerTableStats> = { ...prev.perTable };
  for (const [keyStr, sessionStats] of Object.entries(input.perTable)) {
    const key = Number(keyStr);
    const existing = perTable[key] ?? { questions: 0, correct: 0 };
    perTable[key] = {
      questions: existing.questions + sessionStats.questions,
      correct: existing.correct + sessionStats.correct,
    };
  }

  const { weakestTable, strongestTable } = computeWeakestStrongest(perTable, prev);

  return {
    totalQuestions,
    totalCorrect,
    totalCorrectResponseTimeMs,
    weakestTable,
    strongestTable,
    streakRecord,
    perTable,
  };
}

/**
 * Tabuada mais fraca = menor taxa de acerto entre as que têm amostra
 * mínima (>= MIN_SAMPLE). Mais forte, vice-versa. Empate desempata pela
 * que tem mais amostra. Se nenhuma tabuada atingiu a amostra mínima,
 * mantém os valores anteriores.
 */
const MIN_SAMPLE = 5;

function computeWeakestStrongest(
  perTable: Record<number, PerTableStats>,
  prev: PlayerStatistics,
): { weakestTable: number; strongestTable: number } {
  const eligible = Object.entries(perTable)
    .map(([k, v]) => ({ table: Number(k), ...v }))
    .filter((e) => e.questions >= MIN_SAMPLE);

  if (eligible.length === 0) {
    return { weakestTable: prev.weakestTable, strongestTable: prev.strongestTable };
  }

  let weakest = eligible[0]!;
  let strongest = eligible[0]!;
  for (const e of eligible) {
    const accuracy = e.correct / e.questions;
    const wAcc = weakest.correct / weakest.questions;
    const sAcc = strongest.correct / strongest.questions;
    if (accuracy < wAcc || (accuracy === wAcc && e.questions > weakest.questions)) {
      weakest = e;
    }
    if (accuracy > sAcc || (accuracy === sAcc && e.questions > strongest.questions)) {
      strongest = e;
    }
  }

  return { weakestTable: weakest.table, strongestTable: strongest.table };
}

function deepCloneSave(data: SaveData): SaveData {
  // Records aninhados são "POJO simples" — JSON clone é suficiente.
  return JSON.parse(JSON.stringify(data)) as SaveData;
}

/**
 * Instância global compartilhada entre cenas. Usar singleton aqui evita
 * passar SaveSystem como parâmetro em cada `scene.start()` e mantém a
 * inicialização centralizada (BootScene chama `init()`, todas as outras
 * cenas leem livremente).
 *
 * Em testes, criar instâncias próprias com store em memória (não usar
 * este singleton).
 */
export const saveSystem = new SaveSystem();
